export type OnlineStatus =
	| "draft"
	| "pre-launch"
	| "active"
	| "out-of-stock"
	| "temporarily-unavailable"
	| "discontinued"
	| "inactive";

/** Shopping contexts a product may be bought in, as resolved by search-sync. */
export type Audience =
	"consumer" | "pws" | "guest" | "consultant" | "host" | "agreement";

const STOREFRONT_AUDIENCES: readonly Audience[] = [
	"consumer",
	"pws",
	"guest",
	"consultant"
];

interface Capabilities {
	/** The PDP renders. When false the route redirects to the shop landing page. */
	viewable: boolean;
	purchasable: boolean;
	priceVisible: boolean;
	retired: boolean;
}

/** Adding an OnlineStatus means adding one row here and nothing else. */
const CAPABILITIES: Record<OnlineStatus, Capabilities> = {
	draft: {
		viewable: false,
		purchasable: false,
		priceVisible: false,
		retired: false
	},
	"pre-launch": {
		viewable: true,
		purchasable: false,
		priceVisible: false,
		retired: false
	},
	active: {
		viewable: true,
		purchasable: true,
		priceVisible: true,
		retired: false
	},
	"out-of-stock": {
		viewable: true,
		purchasable: false,
		priceVisible: true,
		retired: false
	},
	"temporarily-unavailable": {
		viewable: true,
		purchasable: false,
		priceVisible: true,
		retired: false
	},
	discontinued: {
		viewable: true,
		purchasable: false,
		priceVisible: false,
		retired: true
	},
	inactive: {
		viewable: false,
		purchasable: false,
		priceVisible: false,
		retired: true
	}
};

// A status this build predates is treated as draft rather than rendered.
const capabilitiesOf = (status: OnlineStatus): Capabilities =>
	CAPABILITIES[status] ?? CAPABILITIES.draft;

export const isViewable = (status: OnlineStatus): boolean =>
	capabilitiesOf(status).viewable;

export const isPurchasable = (status: OnlineStatus): boolean =>
	capabilitiesOf(status).purchasable;

export const isPriceVisible = (status: OnlineStatus): boolean =>
	capabilitiesOf(status).priceVisible;

export const isDiscontinued = (status: OnlineStatus): boolean =>
	capabilitiesOf(status).retired;

export const showsLowStock = (product: {
	onlineStatus: OnlineStatus;
	lowStock?: boolean;
}): boolean => isPurchasable(product.onlineStatus) && product.lowStock === true;

export const isSoldToStorefront = (availableTo: Audience[]): boolean =>
	availableTo.some((audience) => STOREFRONT_AUDIENCES.includes(audience));

/** Asked once, by the catalog read — everything downstream assumes a page exists. */
export const hasStorefrontPage = (product: {
	onlineStatus: OnlineStatus;
	availableTo?: Audience[];
}): boolean =>
	isViewable(product.onlineStatus) &&
	isSoldToStorefront(product.availableTo ?? []);

const AVAILABILITY_LABELS: Partial<Record<OnlineStatus, string>> = {
	"pre-launch": "Coming Soon",
	"out-of-stock": "Out of Stock",
	"temporarily-unavailable": "Currently Unavailable",
	discontinued: "Discontinued",
	inactive: "Discontinued"
};

/** Undefined exactly when the product can be bought, so presence is the signal. */
export const availabilityLabel = (status: OnlineStatus): string | undefined =>
	AVAILABILITY_LABELS[status];
